'use client';
import React from 'react';
import { SLIDE_COUNT, slides } from '@/lib/presentation/slides';
import { goTo, next, prev, usePresentation } from '@/lib/presentation/store';
import { releasePresentation } from './useSlideInput';

const pad = (n: number) => String(n).padStart(2, '0');

function Chevron({ up }: { up?: boolean }) {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true">
      <path
        d={up ? 'M4 10l4-4 4 4' : 'M4 6l4 4 4-4'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Presentation indicator: a quiet vertical rail of ticks (desktop) / hairline segments
 * (mobile), the "03 / 14" counter and previous / next controls.
 */
export function SlideIndicator() {
  const index = usePresentation((s) => s.index);
  const last = index === SLIDE_COUNT - 1;

  return (
    <>
      <nav className="v2-rail" aria-label="Presentation scenes">
        <ol>
          {slides.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                className="v2-tick"
                data-active={i === index}
                data-passed={i < index}
                aria-label={`Scene ${i + 1}: ${s.label}`}
                aria-current={i === index ? 'step' : undefined}
                onClick={() => goTo(i)}
              >
                <span className="v2-tick-label" aria-hidden="true">
                  {s.label}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <div className="v2-counter">
        <p className="v2-counter-text" aria-hidden="true">
          <span className="text-white">{pad(index + 1)}</span>
          <span className="v2-counter-line" />
          <span>{pad(SLIDE_COUNT)}</span>
          <span className="v2-counter-label">{slides[index].label}</span>
        </p>
        <div className="v2-counter-buttons">
          <button
            type="button"
            className="v2-step-button"
            onClick={prev}
            disabled={index === 0}
            aria-label="Previous scene"
          >
            <Chevron up />
          </button>
          <button
            type="button"
            className="v2-step-button"
            onClick={last ? releasePresentation : next}
            aria-label={last ? 'Continue to FAQs and contact' : 'Next scene'}
          >
            <Chevron />
          </button>
        </div>
      </div>

      <div className="v2-progress" aria-hidden="true">
        <span style={{ transform: `scaleX(${(index + 1) / SLIDE_COUNT})` }} />
      </div>
    </>
  );
}
